import { RenderIf } from '../helpers/RenderIf';
import React, { useState } from 'react';
import { T } from '../traks';
import { Input, PhoneInp } from './Input';
import { Role } from './Role';
import { InfoTip } from './InfoTip';
import {
	EmailErrorText,
	NameErrorText,
	PhoneErrorText,
	emailValidator,
} from './Input/InputValidators';

const Header = ({ close, editUser }) => {
	return (
		<div className="d-flex justify-content-between align-items-center">
			<div className="primary fw_600">
				{(!editUser && <T>Add User</T>) || <T>Edit User</T>}
			</div>
			<div onClick={close} className="bb cursor_pointer">
				x
			</div>
		</div>
	);
};

const ShowAsRecipient = ({ user, setUser }) => {
	return (
		<div className="mt-3">
			<div className="fw_600">
				<T>Set visibility as recipients</T>
			</div>
			<div className="m-1">
				<div className="form-switch my-4">
					<input
						className="form-check-input me-5"
						type="checkbox"
						checked={user.show_at_whistle_create}
						onChange={() =>
							setUser({
								...user,
								show_at_whistle_create: !user.show_at_whistle_create,
							})
						}
					/>
					<span>
						<T>Show as assignee at case creation</T>
						<InfoTip>
							<T>
								If active, this user will a possibility as a recipient for the
								case, when a case reporter creates the case.
							</T>
						</InfoTip>
					</span>
				</div>
			</div>
			<div className="m-1">
				<div className="form-switch my-4">
					<input
						className="form-check-input me-5"
						type="checkbox"
						checked={user.show_at_whistle_assign}
						onChange={() =>
							setUser({
								...user,
								show_at_whistle_assign: !user.show_at_whistle_assign,
							})
						}
					/>
					<label>
						<T>Show as assignee at case assignment</T>
						<InfoTip>
							<T>
								If active, this user will a possibility as a recipient for case
								workers when re-assigning a case.
							</T>
						</InfoTip>
					</label>
				</div>
			</div>
		</div>
	);
};

const TextFieldsRow = ({ children, className = '' }) => {
	return (
		<div className={`d-flex flex-row mb-4${' ' + className}`}>
			<div className="w-100 me-4">{children[0]}</div>
			<div className="w-100 ms-4">{children[1]}</div>
		</div>
	);
};

export const UserForm = ({
	signupOnClick,
	roles,
	hideAddUser,
	preSetUser,
	actionType = 'AddUser',
	...props
}) => {
	const [user, setUser] = useState(preSetUser);
	const [showRequiredWarnings, setShowRequiredWarnings] = useState(false);
	const handleRoleChange = (e) => {
		const name = e.target.name;
		if (e.target.checked) {
			const newRoles = user.roles.filter((el) => {
				if (name === 'internal_case_worker') {
					return el !== 'external_case_worker';
				} else if (name === 'external_case_worker') {
					return el !== 'internal_case_worker';
				} else {
					return el;
				}
			});
			setUser({ ...user, roles: [...newRoles, name] });
		} else {
			setUser({
				...user,
				roles: user.roles.filter((el) => el !== name),
			});
		}
	};

	const showRecipientVisibility =
		roles.includes('external_case_worker') ||
		roles.includes('internal_case_worker');

	const formIsValid = () => {
		return (
			user.name.length &&
			user.email.length &&
			user.phone_number.length &&
			emailValidator(user.email)
		);
	};
	const handleSignUp = (user) => {
		if (!formIsValid()) {
			setShowRequiredWarnings(true);
		} else {
			signupOnClick(user);
		}
	};

	return (
		<div className="bg_white sibilus_border rounded-4">
			<div className="m-5">
				<Header close={hideAddUser} editUser={actionType === 'EditUser'} />
			</div>
			<form onSubmit={(e) => e.preventDefault()} className="mx-5 my-3">
				<div className="mt-5">
					<div className="mb-3 border-bottom">
						<TextFieldsRow>
							<div>
								<Input
									dataCy={'name'}
									type={'text'}
									initValue={user.name}
									onChange={(name) => setUser({ ...user, name })}
									placeholder={<T>Name</T>}
								/>
								<NameErrorText
									name={user.name}
									showEmptyWarning={showRequiredWarnings}
								/>
							</div>
							<div>
								<Input
									dataCy={'email'}
									type={'email'}
									initValue={user.email}
									onChange={(email) =>
										setUser({ ...user, email, username: email })
									}
									placeholder={<T>Email</T>}
								/>
								<EmailErrorText
									value={user.email}
									showEmptyWarning={showRequiredWarnings}
								/>
							</div>
						</TextFieldsRow>
						<TextFieldsRow className="mb-4">
							<div>
								<PhoneInp
									initValue={user.phone_number}
									setPhoneNumber={(phone) =>
										setUser({ ...user, phone_number: phone })
									}
								/>
								<PhoneErrorText
									phone={user.phone_number}
									showEmptyWarning={showRequiredWarnings}
								/>
							</div>
							<div className="d-flex">
								<Input
									dataCy={'screenName'}
									initValue={user.screen_name}
									type={'text'}
									onChange={(screen_name) => setUser({ ...user, screen_name })}
									placeholder={<T>Screen name </T>}
								/>
								<InfoTip roleId={'screenNameHelp'}>
									<T>
										This is the name that will be displayed to other users of
										Pentus
									</T>
								</InfoTip>
							</div>
						</TextFieldsRow>
					</div>
					<RenderIf
						condition={actionType === 'EditUser' && showRecipientVisibility}
					>
						<div className="pb-3 border-bottom mb-4">
							<ShowAsRecipient user={user} setUser={setUser} />
						</div>
					</RenderIf>
					<div className="pb-5 border-bottom">
						<div className="fw_600">
							<T>Choose role</T>
						</div>
						{roles.map((role, index) => (
							<Role
								role={role}
								checked={user.roles.find((el) => el === role) !== undefined}
								key={'rr' + index}
								handleRoleChange={handleRoleChange}
							/>
						))}
					</div>
					<div className="py-4 text-end mt-2">
						<button
							onClick={() => hideAddUser()}
							data-cy="signup_cancel_btn"
							className="text-white btn primary sibilus_border me-3 px-3 py-2 hoverable-grey"
						>
							<T>Cancel</T>
						</button>
						<button
							data-cy="signup_btn"
							onClick={() => handleSignUp(user)}
							className="text-white btn btn_green"
						>
							<T>Save User</T>
						</button>
					</div>
				</div>
			</form>
		</div>
	);
};
